import { Reducer } from 'redux';
import actionTypes from './actionTypes';
import { compareArray } from '../helpers/array';
import { EnumStatusesOfKyc } from "../utils/constants";

// Query Params
interface QueryParamsState {
  [key: string]: any; // Adjust type based on your queryParamsInitial
}

// Game Info
type GameInfoState = string;

// Game Box Mission
interface GameBoxMissionState {
  actualData: any[]; // Adjust type based on your gameBoxMissionInitial
  prevData: any[]; // Adjust type based on your gameBoxMissionInitial
}

// Mission
interface Mission {
  missionId: string;
  // Define other fields as per your missionInitial structure
}

type MissionState = Mission[];

// Casino Tournament Next Instances
interface CasinoTournamentInstance {
  id: string;
  // Define other fields as per your casinoTournamentNextInstancesInitial structure
}

interface CasinoTournamentNextInstancesState {
  actualData: { [key: string]: CasinoTournamentInstance };
  prevData: { [key: string]: CasinoTournamentInstance } | null;
}

// Progressive Missions
interface ProgressiveMission {
  id: string;
  // Define other fields as per your progressiveMissionsInitial structure
}

interface ExpiredProgressiveMissionsState {
  data: any[];
  fetched: boolean;
}

const expiredProgressiveMissionsInitial: ExpiredProgressiveMissionsState = {
  data: [],
  fetched: false,
};

type ProgressiveMissionsState = any[];

// Define initial states

const queryParamsInitial: QueryParamsState = {};

const gameInfoInitial: GameInfoState = '';

const gameBoxMissionInitial: GameBoxMissionState = { actualData: [], prevData: [] };

const missionInitial: MissionState = [];

const selectedGameBoxTagInitial = { selected: '' };

const casinoTournamentNextInstancesInitial: CasinoTournamentNextInstancesState = { actualData: {}, prevData: null };

const progressiveMissionsInitial: ProgressiveMissionsState = [];

const racesInitial: any[] = []; // Adjust based on your racesInitial

const miniGamesInitial: [] = []; // Adjust based on your racesInitial

// Reducers

export const queryParams: Reducer<QueryParamsState> = (state = queryParamsInitial, action) => {
  switch (action.type) {
    case actionTypes.SET_QUERY_PARAMS:
      return {
        ...state,
        ...action.payload,
      };
    case actionTypes.RESET_QUERY_PARAMS:
      return queryParamsInitial;
    case actionTypes.LOGIN_SET_QUERY_PARAMS:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
};

export const gameInfo: Reducer<GameInfoState, any> = (state = gameInfoInitial, action) => {
  switch (action.type) {
    case actionTypes.SET_GAME_INFO:
      return action.gameInfo;
    case actionTypes.RESET_GAME_INFO:
      return gameInfoInitial;
    default:
      return state;
  }
};

export const gameBoxMission: Reducer<GameBoxMissionState, any> = (state = gameBoxMissionInitial, action) => {
  switch (action.type) {
    case actionTypes.GAME_BOX_MISSION_SUCCESS:
      return {
        ...state,
        actualData: action.value
      };
    case actionTypes.GAME_BOX_MISSION_UPDATE:
      return {
        actualData: compareArray(state?.actualData, action.value),
        prevData: state?.actualData || [],
      };
    case actionTypes.GAME_BOX_MISSION_FAIL:
      return gameBoxMissionInitial;
    default:
      return state;
  }
};

export const mission: Reducer<MissionState, any> = (state = missionInitial, action) => {
  switch (action.type) {
    case actionTypes.MISSION_SUCCESS:
      return action.value;
    case actionTypes.MISSION_UPDATE: {
      const { field, value, missionId } = action.value;
      let newState = [...state];
      if (newState.length && field && value && missionId) {
        let currentMission = newState.find((m: Mission) => m.missionId === missionId);
        if (currentMission) {
          currentMission[field] = value;
          return newState;
        }
      }
      return state;
    }
    case actionTypes.UPDATE_MISSION_TOTAL_WIN_AND_FREE_SPIN_COUNT: {
      const { value, missionId } = action.value;
      let newState = [...state];
      if (newState.length && value && missionId) {
        let currentMission = newState.find((m: Mission) => m.missionId === missionId);
        if (currentMission) {
          Object.assign(currentMission, value);
          return newState;
        }
      }
      return state;
    }
    case actionTypes.MISSION_FAIL:
      return missionInitial;
    default:
      return state;
  }
};

export const selectedGameBoxTag: Reducer<{ selected: string }, any> = (state = selectedGameBoxTagInitial, action) => {
  switch (action.type) {
    case actionTypes.SET_GAME_BOX_TAG:
      return { ...state, selected: action.selected };
    default:
      return state;
  }
};

export const casinoTournamentNextInstances: any = (state: any = casinoTournamentNextInstancesInitial, action: any) => {
  switch (action.type) {
    case actionTypes.CASINO_TOURNAMENT_NEXT_INSTANCE_SUCCESS:
      const newState = { ...state.actualData };
      newState[action.value.id] = action.value;
      return {
        actualData: newState,
        prevData: state?.actualData || null
      };
    case actionTypes.CASINO_TOURNAMENT_NEXT_INSTANCE_UPDATE:
      return {
        actualData: [...state, ...action.value],
        prevData: state?.actualData || null
      }
    case actionTypes.CASINO_TOURNAMENT_NEXT_INSTANCE_RESET_PREV_DATA:
      return {
        actualData: state.actualData,
        prevData: []
      }
    case actionTypes.CASINO_TOURNAMENT_NEXT_INSTANCE_FAILURE:
      return casinoTournamentNextInstancesInitial;
    default:
      return state;
  }
};

export const progressiveMissions: Reducer<ProgressiveMissionsState, any> = (state = progressiveMissionsInitial, action) => {
  switch (action.type) {
    case actionTypes.PROGRESSIVE_MISSIONS_SUCCESS:
      return action.payload;
    case actionTypes.PROGRESSIVE_MISSIONS_UPDATE:
      let newState = [...state];
      action?.payload?.map((mission: any) => {
        let currentProgressiveMission = (newState?.find((m: ProgressiveMission) => m.id === mission.progressiveMissionId))?.submissions?.find((m: any) => m.status === "inProgress");
        if (currentProgressiveMission) {
          currentProgressiveMission['progress'] = mission.progress
        }
      })
      return newState;
    case actionTypes.PROGRESSIVE_MISSIONS_CHAIN_UPDATE:
      let chainState = [...state];
      let current: any = chainState?.find((m: ProgressiveMission) => m.id === action.missionChain?.[0]?.progressive_mission_id);
      if (current) {
        current.submissions = action.missionChain;
        current.current_index = action.currentIndex;
      }
      return chainState;
    case actionTypes.PROGRESSIVE_MISSIONS_FAIL:
      return progressiveMissionsInitial;
    default:
      return state;
  }
};

export const expiredProgressiveMissions: Reducer<ExpiredProgressiveMissionsState, any> = (state = expiredProgressiveMissionsInitial, action) => {
  switch (action.type) {
    case actionTypes.EXPIRED_PROGRESSIVE_MISSIONS_SUCCESS:
      return { data: action.payload, fetched: true };
    case actionTypes.EXPIRED_PROGRESSIVE_MISSIONS_CLEAR:
      return expiredProgressiveMissionsInitial;
    default:
      return state;
  }
};

export const races: Reducer<any[], any> = (state = racesInitial, action) => {
  switch (action.type) {
    case actionTypes.RACES_SUCCESS:
      return action.payload;
    case actionTypes.RACES_FAILURE:
      return racesInitial;
    default:
      return state;
  }
};

export const miniGames: Reducer<any[], any> = (state = miniGamesInitial, action) => {
  switch (action.type) {
    case actionTypes.MINI_GAMES_SUCCESS:
      return action.payload;
    case actionTypes.MINI_GAMES_FAILURE:
      return miniGamesInitial;
    default:
      return state;
  }
};

//gameUrl
const gameUrlInitial = '';

export const gameUrl = (state = gameUrlInitial, action: { type: any; url: any; }) => {
  switch (action.type) {
    case actionTypes.SET_GAME_URL:
      return action.url;
    case actionTypes.RESET_GAME_URL:
      return gameUrlInitial;
    default:
      return state;
  }
}

//gameBoxMissionCount

const gameBoxMissionCountInitial = 0;

export const gameBoxMissionCount = (state = gameBoxMissionCountInitial, action: { type: any; value: any; }) => {
  switch (action.type) {
    case actionTypes.SET_GAME_BOX_MISSION_COUNT:
      return action.value;
    case actionTypes.RESET_GAME_BOX_MISSION_COUNT:
      return gameBoxMissionCountInitial;
    default:
      return state;
  }
}


//races
const racesAccountResultInitial = {};

export const racesAccountResult = (state = racesAccountResultInitial, action: { type: any; payload: { accountResult: any; }; }) => {
  switch (action.type) {
    case actionTypes.RACES_LEADERBOARD_SUCCESS:
      return action.payload?.accountResult || {};
    case actionTypes.RACES_LEADERBOARD_FAILURE:
      return racesAccountResultInitial;
    default:
      return state;
  }
};

//raceLeaderBoardLimit
const raceLeaderBoardLimitInitial = 20;

export const raceLeaderBoardLimit = (state = raceLeaderBoardLimitInitial, action: { type: any; limit: any; }) => {
  switch (action.type) {
    case actionTypes.RACES_LEADERBOARD_REQUEST:
      return action.limit || state;
    default:
      return state;
  };
};

const initialState = {};

export const raceTemplateUpdate = (state = initialState, action: { type: any; payload: any; error: any; }) => {
  switch (action.type) {
    case actionTypes.SAVE_TEMPLATE_MSG_SUCCESS:
      return action.payload;
    case actionTypes.SAVE_TEMPLATE_MSG_FAILURE:
      return {
        ...state,
        error: action.error,
      };
    default:
      return state;
  }
};


//gameBoxRaffleTicketsData
const gameBoxRaffleTicketsDataInitial = [{ currency: '', total_prize: 0, tickets_count: null, expire_time: null }];

export const gameBoxRaffleTickets = (state = gameBoxRaffleTicketsDataInitial, action: { type: any; value: any; }) => {
  switch (action.type) {
    case actionTypes.GAME_BOX_JACKPOT_TICKETS_SUCCESS:
      return action.value
    default:
      return state;
  }
}

//gameBoxView
const gameBoxViewInitial = '';

export const gameBoxView = (state = gameBoxViewInitial, action: { type: any; payload: any; }) => {
  switch (action.type) {
    case actionTypes.CHANGE_GAME_BOX_VIEW:
      return action.payload;
    default:
      return state;
  }
};


const gameBoxKycStatusInitial = null;

export const gameBoxKycStatus = (state = gameBoxKycStatusInitial, action: { type: string; payload: string | undefined | null; }) => {
  switch (action.type) {
    case actionTypes.CHANGE_GAME_BOX_KYC:
      if (action.payload === undefined) {
        return EnumStatusesOfKyc['REJECTED']
      } else {
        return action.payload
      }
    default:
      return state;
  }
};

const gameBoxLimitsStatusInitial = false;

export const gameBoxLimitsStatus = (state = gameBoxLimitsStatusInitial, action: { type: string; payload: boolean; }) => {
  switch (action.type) {
    case actionTypes.CHANGE_GAME_BOX_LIMITS:
      return action.payload;
    default:
      return state;
  }
};


//gameBoxRaffle
const gameBoxRaffleInitial = { actualData: null, prevData: null };

export const gameBoxRaffle = (state = gameBoxRaffleInitial, action: { type: any; value: any; }) => {
  switch (action.type) {
    case actionTypes.GAME_BOX_JACKPOT_SUCCESS:
      return {
        ...state,
        actualData: action.value
      };
    case actionTypes.GAME_BOX_JACKPOT_UPDATE:
      return {
        prevData: state?.actualData || null,
        actualData: {
          ...(state.actualData || {}),
          ...action.value,
        },
      };
    case actionTypes.GAME_BOX_JACKPOT_RESET_PREV_DATA:
      return {
        prevData: null,
        actualData: state.actualData,
      };
    case actionTypes.GAME_BOX_JACKPOT_FAIL:
      return gameBoxRaffleInitial;
    default:
      return state;
  }
};

//flags
const flagsInitial = {
  globalGameBoxWidgetIsOpen: false,
  availableGamesListIsOpen: false,
  raffleDrawRulesIsOpen: false,
  leaderBoardPending: false
};

export const flags = (state = flagsInitial, action: { type: any; }) => {
  switch (action.type) {
    case actionTypes.RACES_LEADERBOARD_REQUEST:
      return {...state, leaderBoardPending: true};
    case actionTypes.RACES_LEADERBOARD_SUCCESS:
    case actionTypes.RACES_LEADERBOARD_FAILURE:
      return {...state, leaderBoardPending: false};
    case actionTypes.TOGGLE_AVAILABLE_GAMES_LIST:
      return { ...state, availableGamesListIsOpen: !state.availableGamesListIsOpen };
    case actionTypes.TOGGLE_RAFFLE_DRAW_RULES:
      return { ...state, raffleDrawRulesIsOpen: !state.raffleDrawRulesIsOpen };
    default:
      return state;
  }
}

//races
const racesLeaderboardInitial = {
  isLoading: false,
  data: [],
};

export const racesLeaderboard = (state = racesLeaderboardInitial, action: { type: any; payload: { leaderBoard: any; }; }) => {
  switch (action.type) {
    case actionTypes.RACES_LEADERBOARD_LOADER:
      return {
        ...state,
        isLoading: true,
      };
    case actionTypes.RACES_LEADERBOARD_SUCCESS:
      return {
        ...state,
        data: [
          ...state.data,
          ...action?.payload?.leaderBoard,
        ],
        isLoading: false,
      };
    case actionTypes.RACES_LEADERBOARD_RESET:
    case actionTypes.RACES_LEADERBOARD_FAILURE:
      return racesLeaderboardInitial;
    default:
      return state;
  }
};

//messages
const messagesInitial: never[] = [];

export const messages = (state = messagesInitial, action: { type: any; arrMessages: any; messageId: any; }) => {
  switch (action.type) {
    case actionTypes.GET_MESSAGES_SUCCESS:
      return action.arrMessages;
    case actionTypes.READ_MESSAGE_SUCCESS:
      let newState = [...state];
      const message: any = newState.find((msg: any) => msg._id === action.messageId);
      message.status = 'read';
      return newState;
    default:
      return state;
  }
};

//actualLanguage
const actualLanguageInitial = null;

export const actualLanguage = (state = actualLanguageInitial, action: { type: any; payload: any }) => {
  switch (action.type) {
    case actionTypes.SET_ACTUAL_LANGUAGE:
      return action.payload;
    case actionTypes.RESET_ACTUAL_LANGUAGE:
      return actualLanguageInitial;
    default:
      return state;
  }
};